/** Canvas2D overlay rendering: speed zones, power range, AI debug, sectors. */
import { Util } from '../../util.js';
import { SPEED_ZONE_PULSE_PERIOD, SPEED_ZONE_RGB, ZONE_GRADIENT, pulse01 } from '../constants.js';

const RENDER_DEBUG = false;

/**
 * @param {import('../../renderer.js').Canvas2DRenderer} r
 */
export function renderPowerRangeIndicator(r) {
    if (r.game.getGameState() !== 1) return;

    const selectedPower = r.game.getSelectedPower();
    const indicator = r.ui.getPowerRangeIndicator();

    if (!selectedPower || !indicator) return;
    if (!Number.isFinite(selectedPower.effectDistance)) return;
    const usesRemaining = r.game.getPowerUsesRemaining(selectedPower.id);
    if (usesRemaining !== null && usesRemaining < 1) return;

    const zoom = r.game.getLevelZoomScale ? r.game.getLevelZoomScale() : 1;
    r.context.save();
    r.context.globalAlpha = indicator.alpha;
    r.context.strokeStyle = selectedPower.indicatorColor || 'rgba(255, 255, 255, 0.6)';
    r.context.lineWidth = 1.5;
    r.context.beginPath();
    r.context.arc(indicator.x, indicator.y, selectedPower.effectDistance * zoom, 0, Math.PI * 2);
    r.context.closePath();
    r.context.stroke();
    r.context.restore();
}

/**
 * @param {import('../../renderer.js').Canvas2DRenderer} r
 */
export function renderSpeedZones(r) {
    if (!r.entitySystem.speedZones || r.entitySystem.speedZones.length === 0) return;
    r.context.save();

    const pulseOpacity = pulse01((r.entitySystem.time % SPEED_ZONE_PULSE_PERIOD) / SPEED_ZONE_PULSE_PERIOD);

    for (let i = 0; i < r.entitySystem.speedZones.length; i += 1) {
        const zone = r.entitySystem.speedZones[i];
        const szd = zone.zoneData;
        if (!szd) continue;
        const zoneRadius = zone.getSlowZoneRadiusAt ? zone.getSlowZoneRadiusAt(r.entitySystem.time) : szd.radius;
        if (zoneRadius <= 0) continue;

        const rgb = szd.colorRGB || SPEED_ZONE_RGB;
        const red = rgb[0], grn = rgb[1], blu = rgb[2];
        const isFast = szd.multiplier > 1;

        const gradient = r.context.createRadialGradient(zone.x, zone.y, 0, zone.x, zone.y, zoneRadius);

        if (isFast) {
            for (const [offset, alpha] of ZONE_GRADIENT.fast) {
                gradient.addColorStop(offset, `rgba(${red}, ${grn}, ${blu}, ${pulseOpacity * alpha})`);
            }
        } else {
            gradient.addColorStop(0, `rgba(0, 0, 0, ${pulseOpacity * ZONE_GRADIENT.slowCenterAlpha})`);
            gradient.addColorStop(1, `rgba(${red}, ${grn}, ${blu}, ${pulseOpacity * ZONE_GRADIENT.slowEdgeAlpha})`);
        }

        r.context.fillStyle = gradient;
        r.context.beginPath();
        r.context.arc(zone.x, zone.y, zoneRadius, 0, Math.PI * 2);
        r.context.closePath();
        r.context.fill();
    }
    r.context.restore();
}

/**
 * @param {import('../../renderer.js').Canvas2DRenderer} r
 * @param {object} entity
 */
export function renderAiDebugStateMarker(r, entity) {
    const state = entity.aiDebugState || '';
    if (!state) return;
    const markerColor = r.aiStateColors[state] || '#FFFFFF';
    const markerSize = Math.max(6, entity.size + 4);
    r.context.save();
    r.context.strokeStyle = markerColor;
    r.context.lineWidth = 0.5;
    r.context.strokeRect(
        entity.x - markerSize * 0.5,
        entity.y - markerSize * 0.5,
        markerSize,
        markerSize
    );
    r.context.restore();
}

/**
 * @param {import('../../renderer.js').Canvas2DRenderer} r
 */
export function renderAiTargetLines(r) {
    r.context.save();
    r.context.lineWidth = 0.8;
    for (let i = 0; i < r.entitySystem.units.length; i += 1) {
        const unit = r.entitySystem.units[i];
        if (unit.combat && unit.combat.target) {
            const target = unit.combat.target;
            const targetIsDestroyed = !!(target && target.health &&
                target.health.isDestroyed && target.health.isDestroyed());
            if (target && !targetIsDestroyed) {
                r.context.strokeStyle = 'rgba(255, 80, 210, 0.25)';
                r.context.beginPath();
                r.context.moveTo(unit.x, unit.y);
                r.context.lineTo(target.x, target.y);
                r.context.stroke();
            }
        }

        if (unit.movement) {
            const movementDistance = Util.distance(unit.x, unit.y, unit.destX, unit.destY);
            if (movementDistance > Math.max(3, unit.size * 0.35)) {
                r.context.strokeStyle = 'rgba(80, 220, 255, 0.25)';
                r.context.beginPath();
                r.context.moveTo(unit.x, unit.y);
                r.context.lineTo(unit.destX, unit.destY);
                r.context.stroke();
            }
            const m = 2;
            r.context.strokeStyle = 'rgba(80, 220, 255, 0.5)';
            r.context.beginPath();
            r.context.moveTo(unit.destX - m, unit.destY - m);
            r.context.lineTo(unit.destX + m, unit.destY + m);
            r.context.moveTo(unit.destX + m, unit.destY - m);
            r.context.lineTo(unit.destX - m, unit.destY + m);
            r.context.stroke();
        }
    }
    r.context.restore();
}

/**
 * @param {import('../../renderer.js').Canvas2DRenderer} r
 */
export function renderSectors(r) {
    for (let sx = 0; sx < r.gridSystem.sectors.length; sx += 1) {
        for (let sy = 0; sy < r.gridSystem.sectors[sx].length; sy += 1) {
            const sector = r.gridSystem.sectors[sx][sy];
            if (RENDER_DEBUG) {
                r.context.strokeStyle = '#0F0';
                r.context.lineWidth = 0.15;
                r.context.strokeRect(
                    sector.sx * r.gridSystem.gridSize,
                    sector.sy * r.gridSystem.gridSize,
                    r.gridSystem.gridSize,
                    r.gridSystem.gridSize
                );
                r.context.font = "normal 7px 'Lucida Console'";
                r.context.textAlign = 'left';
                r.context.fillStyle = '#0F0';
                r.context.fillText(
                    sector.entities.length,
                    sector.sx * r.gridSystem.gridSize,
                    sector.sy * r.gridSystem.gridSize + 7
                );
            }
        }
    }
}
